angular.module('acme.common.service.utils', [])
  .service('UtilsService', UtilsService);

/**
 * Service containing some utility methods
 * @constructor
 * @param {Object} $timeout - angular $timeout service
 * @param {Object} $q - angular $q service
 * @return {UtilsService} instance
 */
function UtilsService($timeout, $q) {
  return {
    clamp: clamp,
    debounce: debounce,
    getDateIfValid: getDateIfValid,
    removeUndefinedProperties: removeUndefinedProperties,
    unwrapAcmeResponse: unwrapAcmeResponse,
    eventAlreadyFired:eventAlreadyFired,
    zip: zip
  };

  /**
   * Utility method for clamping a value
   * @memberOf UtilsService
   * @public
   * @param {Number} value - the value to be tested
   * @param {Number} min - the minimum value
   * @param {Number} max - the maximum value
   * @return {Number} the clamped result
   */
  function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }

  /**
   * Utility method for removing undefined properties of an object
   * It only cleans first level properties
   * @memberOf UtilsService
   * @public
   * @param {Object} data - the object to be cleaned
   * @param {Boolean} isOnChanges - if it is true it will try to look for the currentValue key
   * @return {Object} cleaned object
   */
  function removeUndefinedProperties(data, isOnChanges) {
    var _data = data || {};

    return Object.keys(_data).reduce(function (result, key) {
      var value = _data[key];

      if (isOnChanges) {
        value = _data[key].currentValue;
      }

      if (typeof value !== 'undefined') {
        result[key] = value;
      }

      return result;
    }, {});
  }

  /**
   * Unwrap ACME http response
   *
   * @memberOf UtilsService
   * @public
   * @param {Object} response
   * @return {Object} payload
   */
  function unwrapAcmeResponse(response) {
    return response.data.payload;
  }

  /**
   * Zip arrays of data
   *
   * @memberOf UtilsService
   * @public
   * @param {Array*} args - Arrays to be zipped
   * @return {Array} - Zipped array
   * @example
   *    zip([1,2,3],[4,5,6]) -> [[1,4],[2,5],[3,6]]
   *    zip([1,2,3],[5]) -> [[1,5]]
   *    zip([1,2,3]) -> [[1], [2], [3]]
   *
   */
  function zip() {
    var arrs = [].slice.call(arguments),
      resultLength = null;

    resultLength = Math.min.apply(null, arrs.map(function (item) {
      return item.length;
    }));

    return Array.apply(null, new Array(resultLength))
      .map(
        function (unused, i) {
          return arrs.map(function (item) {
            return item[i];
          });
        }
      );
  }

  /**
   * Utility method for creating a debounce function that returns a promise
   * @memberOf UtilsService
   * @public
   * @param {Function} func - the function to be debounced
   * @param {Number} wait - number of ms before firing it
   * @param {Boolean} immediate - indicates whether it should be fired straight away
   * @return {Function} - debounced function
   */
  function debounce(func, wait, immediate) {
    var timeout,
      deferred = $q.defer();

    return function() {
      var context = this,
        args = arguments,
        later = function() {
          timeout = null;
          if (!immediate) {
            deferred.resolve(func.apply(context, args));
            deferred = $q.defer();
          }
        },
        callNow = immediate && !timeout;

      if (timeout) {
        $timeout.cancel(timeout);
      }

      timeout = $timeout(later, wait);

      if (callNow) {
        deferred.resolve(func.apply(context, args));
        deferred = $q.defer();
      }

      return deferred.promise;
    };
  }

  /**
   * Get a Date object if the provided possible date (string) can be parsed as a date
   *
   * @param {string} possibleDate
   * @returns {Date}
   */
  function getDateIfValid(possibleDate) {
    if (possibleDate) {
      var date = Date.parse(possibleDate);

      if (!isNaN(date)) {
        return new Date(possibleDate);
      }
    }
  }

    /**
    * Workaround to prevent an ng-click event fired multiple times when aggrid + DataGrid component used.
    *
    * @param $event
    * @returns True, if the same event was already fired,
    * so preventative measure can be taken to avoid the same code executed as many times as 15???
    */
    function eventAlreadyFired ($event) {
      if ($event.timeStamp === this.timestampOfEventFired) {
          return true;
      }
      this.timestampOfEventFired = $event.timeStamp;
      return false;
    }
}

UtilsService.$inject = ['$timeout', '$q'];
